import { Component, Signal, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, map, switchMap, tap } from 'rxjs';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { ConvertToFleetIdTagRequest, IdTagService } from 'src/app/shared/services/id-tag.service';
import { IdTag } from '../id-tag-model';
import { Department, DepartmentSearchEvent } from '../../department/department.model';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { AccessGroup, AccessGroupSearchEvent } from '../../access-groups/access-group.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';

interface IConvertToFleetIdTagForm {
  selectedDepartment: FormControl<Department | null>;
  selectedAccessGroups: FormControl<string[]>;
  agreementNumber: FormControl<number | null>;
}

@Component({
  selector: 'adm-id-tag-convert-to-fleet-id-tag-modal',
  templateUrl: './id-tag-convert-to-fleet-id-tag-modal.component.html',
  styleUrls: ['./id-tag-convert-to-fleet-id-tag-modal.component.scss'],
  imports: [
    CustomDropDownArrowDirective,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    FeedbackInfoComponent,
    AdmG4AgreementNumberComponent,
  ],
})
export class IdTagConvertToFleetIdTagModalComponent {
  dialogRef = inject<MatDialogRef<IdTagConvertToFleetIdTagModalComponent>>(MatDialogRef);
  data = inject<{ idTagId: number }>(MAT_DIALOG_DATA);
  private idTagService = inject(IdTagService);
  private departmentService = inject(DepartmentService);
  private snackbarService = inject(SnackbarService);
  private acessGroupService = inject(AccessGroupService);

  public convertToFleetIdTagForm: FormGroup<IConvertToFleetIdTagForm> = new FormGroup<IConvertToFleetIdTagForm>({
    selectedDepartment: new FormControl<Department | null>(null),
    selectedAccessGroups: new FormControl<string[]>([], { nonNullable: true }),
    agreementNumber: new FormControl<number | null>(null, { validators: [Validators.required] }),
  });
  public saving = signal<boolean>(false);
  public wasSaved = false;

  constructor() {
    if (!this.data?.idTagId) {
      throw new Error('IdTagConvertToFleetIdTagModalComponent requires idTagId to be set in MAT_DIALOG_DATA');
    }
  }

  idTagData: Signal<{ idTag: IdTag; accessGroups: AccessGroup[]; departments: Department[] } | null> = toSignal(
    this.idTagService.getIdTag(this.data.idTagId).pipe(
      switchMap(idTag => {
        const accessGroupSearchEvent = <AccessGroupSearchEvent>{
          organizationId: idTag.organizationId,
          carType: AccessGroupCarType.CompanyCar,
          paging: {
            pageIndex: 0,
            pageSize: 100,
          },
        };
        const depSearchEvent = <DepartmentSearchEvent>{
          organizationId: idTag.organizationId,
          paging: {
            pageIndex: 0,
            pageSize: 1000,
          },
        };
        return combineLatest([
          this.acessGroupService.getAccessGroups(accessGroupSearchEvent),
          this.departmentService.getDepartments(depSearchEvent),
        ]).pipe(
          map(([accessGroups, departments]) => {
            if (accessGroups.items.length > 0) {
              this.convertToFleetIdTagForm.controls.selectedAccessGroups.enable();
            }

            this.convertToFleetIdTagForm.patchValue({
              selectedDepartment: departments.items.filter(d => d.id === idTag.departmentId)[0],
            });

            return { idTag, accessGroups: accessGroups.items, departments: departments.items };
          })
        );
      })
    ),
    { initialValue: null }
  );

  compareObjects(ag1: AccessGroup, ag2: AccessGroup): boolean {
    return ag1?.identifier === ag2?.identifier;
  }

  getFirstAccessGroupName = () => {
    const data = this.idTagData();
    if (!data) {
      return '';
    }

    return this.convertToFleetIdTagForm.controls.selectedAccessGroups.value.length === 0
      ? ''
      : data.accessGroups.find(ag => ag.identifier === this.convertToFleetIdTagForm.controls.selectedAccessGroups.value[0])?.name;
  };

  public saveConvertToFleetIdTag() {
    const formValue = this.convertToFleetIdTagForm.getRawValue();
    if (!formValue.selectedDepartment) {
      return;
    }

    const request = {
      departmentId: formValue.selectedDepartment.id,
      accessGroupIdentifiers: formValue.selectedAccessGroups,
      agreementNumber: formValue.agreementNumber,
    } as ConvertToFleetIdTagRequest;

    this.idTagService
      .convertToFleetIdTagForm(this.data.idTagId, request)
      .pipe(tap(() => this.saving.set(true)))
      .subscribe({
        next: () => {
          this.wasSaved = true;
          const selectedDepartmentName = this.convertToFleetIdTagForm.controls.selectedDepartment.value?.name ?? '';
          this.snackbarService.success(`Brikken blev konverteret til flådebrik og er tilknyttet afdeling ${selectedDepartmentName}`);
        },
        error: err => {
          this.snackbarService.error(`Der skete fejl under konvertering (${err.error.code})`);
        },
        complete: () => {
          this.saving.set(false);
          this.close();
        },
      });
  }

  public close() {
    if (this.wasSaved) {
      this.dialogRef.close(this.idTagData()?.idTag);
    } else {
      this.dialogRef.close();
    }
  }
}
